import { useEffect, useState } from "react";

const screens = {
	sm: "640px",
	md: "768px",
	lg: "1024px",
	xl: "1280px",
	"2xl": "1536px",
};

type Breakpoint = "base" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

/**
 * Checks whether a particular Tailwind CSS viewport size applies.
 *
 * @param size The size to check, which must either be included in Tailwind CSS's
 * list of default screen sizes, or added to the Tailwind CSS config file.
 *
 * @returns A boolean indicating whether the viewport size applies.
 */
export const useBreakpoint = (size: "sm" | "md" | "lg" | "xl" | "2xl") => {
	const [matches, setMatches] = useState(
		typeof window !== "undefined"
			? window.matchMedia(`(min-width: ${screens[size]})`).matches
			: true
	);

	useEffect(() => {
		const breakpoint = window.matchMedia(`(min-width: ${screens[size]})`);

		setMatches(breakpoint.matches);

		const handleChange = (value: MediaQueryListEvent) =>
			setMatches(value.matches);

		breakpoint.addEventListener("change", handleChange);
		return () => breakpoint.removeEventListener("change", handleChange);
	}, [size]);

	return matches;
};

/**
 * Returns the current breakpoint based on the viewport width.
 *
 * @returns The current breakpoint name ("base", "sm", "md", "lg", "xl", "2xl", or "3xl").
 */
export const useCurrentBreakpoint = (): Breakpoint => {
	const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
		if (typeof window === "undefined") return "base";

		const width = window.innerWidth;
		if (width >= 1920) return "3xl";
		if (width >= 1536) return "2xl";
		if (width >= 1280) return "xl";
		if (width >= 1024) return "lg";
		if (width >= 768) return "md";
		if (width >= 640) return "sm";
		return "base";
	});

	useEffect(() => {
		const updateBreakpoint = () => {
			const width = window.innerWidth;
			if (width >= 1920) {
				setBreakpoint("3xl");
			} else if (width >= 1536) {
				setBreakpoint("2xl");
			} else if (width >= 1280) {
				setBreakpoint("xl");
			} else if (width >= 1024) {
				setBreakpoint("lg");
			} else if (width >= 768) {
				setBreakpoint("md");
			} else if (width >= 640) {
				setBreakpoint("sm");
			} else {
				setBreakpoint("base");
			}
		};

		updateBreakpoint();
		window.addEventListener("resize", updateBreakpoint);
		return () => window.removeEventListener("resize", updateBreakpoint);
	}, []);

	return breakpoint;
};

/**
 * Returns the orientation of an element based on its width and height.
 *
 * @param elementRef A ref to the element to observe.
 * @returns "landscape" if width > height, "portrait" otherwise.
 */
export const useElementOrientation = (
	elementRef: React.RefObject<HTMLElement>
): "landscape" | "portrait" => {
	const [orientation, setOrientation] = useState<"landscape" | "portrait">(
		"portrait"
	);

	useEffect(() => {
		if (!elementRef.current) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				setOrientation(width > height ? "landscape" : "portrait");
			}
		});

		resizeObserver.observe(elementRef.current);
		return () => resizeObserver.disconnect();
	}, [elementRef]);

	return orientation;
};

/**
 * Returns the screen orientation based on window dimensions.
 *
 * @returns "landscape" if width > height, "portrait" otherwise.
 */
export const useScreenOrientation = (): "landscape" | "portrait" => {
	const [orientation, setOrientation] = useState<"landscape" | "portrait">(
		() => {
			if (typeof window === "undefined") return "portrait";
			return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
		}
	);

	useEffect(() => {
		const updateOrientation = () => {
			setOrientation(
				window.innerWidth > window.innerHeight ? "landscape" : "portrait"
			);
		};

		updateOrientation();
		window.addEventListener("resize", updateOrientation);
		return () => window.removeEventListener("resize", updateOrientation);
	}, []);

	return orientation;
};
